import { AccountType, type UserAccountInfo } from '@lub0v/santa-utils';
import { useTranslation } from 'react-i18next';
import { Stack, showToast } from '@src/components';
import { SocialLinkCard, type SocialLinkKind } from '@src/components/SocialLinkCard/SocialLinkCard';
import { linkWithGoogle, linkWithTelegram, linkWithVk } from '@src/pages/auth/auth';
import { isDotCom } from '@src/site';
import { santaClient } from '@src/state/client';

export interface SocialLinksProps {
  account: UserAccountInfo;
  onAccountChange: (account: UserAccountInfo) => void;
}

export function SocialLinks({ account, onAccountChange }: SocialLinksProps) {
  const { t } = useTranslation();
  const accounts = account.accounts || [];
  const showGoogle = isDotCom() || accounts.some((item) => item.accountType === AccountType.GOOGLE);
  const showVk = !isDotCom();
  const showTg = isDotCom() || accounts.some((item) => item.accountType === AccountType.TG);

  async function unlink(type: AccountType) {
    const res = await santaClient.unlinkAccount({ type });
    if (res.ok && res.data) {
      onAccountChange(res.data);
    }
  }

  async function connect(kind: SocialLinkKind) {
    const error =
      kind === 'google'
        ? await linkWithGoogle()
        : kind === 'vk'
          ? await linkWithVk()
          : await linkWithTelegram();
    if (error) {
      showToast(
        t(error === 'login.telegramUnavailable' ? 'login.telegramUnavailable' : 'switcher.actionError'),
      );
    }
  }

  function card(
    type: AccountType,
    kind: SocialLinkKind,
    title: string,
    testId: string,
  ) {
    const linked = accounts.some((item) => item.accountType === type);
    return (
      <SocialLinkCard
        kind={kind}
        title={title}
        hint={linked ? t('account.socialConnected') : t('account.socialDisconnected')}
        actionLabel={linked ? t('account.socialDisconnect') : t('account.socialConnect')}
        onAction={() => {
          if (linked) {
            void unlink(type);
            return;
          }
          void connect(kind);
        }}
        testId={testId}
      />
    );
  }

  return (
    <Stack gap={3} wide>
      {showGoogle ? card(AccountType.GOOGLE, 'google', 'Google', 'account-social-google') : null}
      {showVk ? card(AccountType.VK, 'vk', t('account.socialVk'), 'account-social-vk') : null}
      {showTg ? card(AccountType.TG, 'telegram', 'Telegram', 'account-social-tg') : null}
    </Stack>
  );
}
